import { useState } from 'react'
import {
  Grade as RecognitionsIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material'
import { Box } from '@mui/material'
import { teal } from '@mui/material/colors'
import { useMutation } from 'react-query'
import { FormattedDate, useIntl } from 'react-intl'

import ManageRecognition from './manageRecognition/ManageRecognition'
import { CompanyAdminLayout, ConfirmationDialog } from 'components'
import { API } from 'core/requests'
import { UserPermission } from 'enums/user'
import { formatToUSD } from 'utils/currency'
import { getBoolean, getNotApplicable } from 'utils/tables/dataTypes'

const initialResetDialog = { isOpen: false, data: null as any }

export default function Recognitions() {
  const intl = useIntl()

  const [resetDialog, setResetDialog] = useState(initialResetDialog)

  return (
    <>
      <CompanyAdminLayout
        slug='recognitions'
        title={intl.formatMessage({ id: 'recognitions.title' })}
        description={intl.formatMessage({ id: 'recognitions.description' })}
        color={teal[800]}
        requestUrl='recognitions'
        tableProps={{
          columns: [
            {
              title: intl.formatMessage({ id: 'recognitions.fields.program' }),
              field: 'name',
              editable: 'never',
            },
            {
              title: intl.formatMessage({
                id: 'recognitions.fields.hasReward',
              }),
              field: 'has_reward',
              editable: 'never',
              render: (rowData: any) => getBoolean(rowData.has_reward),
            },
            {
              title: intl.formatMessage({
                id: 'recognitions.fields.rewardAmount',
              }),
              field: 'maximum_reward_amount_for_each_recognition',
              editable: 'never',
              render: (rowData: any) =>
                +rowData.maximum_reward_amount_for_each_recognition ? (
                  <Box
                    component='span'
                    sx={{ color: 'success.main', fontWeight: 600 }}
                  >
                    {formatToUSD(
                      rowData.maximum_reward_amount_for_each_recognition,
                    )}
                  </Box>
                ) : (
                  getNotApplicable()
                ),
            },
            {
              title: intl.formatMessage({
                id: 'recognitions.fields.timeframe',
              }),
              field: 'time_frame_start_at',
              editable: 'never',
              render: (rowData: any) =>
                rowData.time_frame_start_at && rowData.time_frame_end_at ? (
                  <>
                    <FormattedDate
                      value={rowData.time_frame_start_at}
                      year='numeric'
                      month='numeric'
                      day='numeric'
                    />{' '}
                    -{' '}
                    <FormattedDate
                      value={rowData.time_frame_end_at}
                      year='numeric'
                      month='numeric'
                      day='numeric'
                    />
                  </>
                ) : (
                  getNotApplicable()
                ),
            },
          ],
          hasStatusSwitch: true,
          actions: [
            {
              icon: () => <ResetIcon />,
              tooltip: () =>
                'Reset recognition count — restarts the usage counter without changing the limit rule. Past recognitions stay in history.',
              hidden: (row: any) =>
                !(
                  row?.has_annual_limit ||
                  row?.has_recurrence ||
                  row?.has_maximum_number_of_recognitions ||
                  row?.has_recurrence_limit_per_employee
                ),
              onClick: (_: any, event: any) =>
                setResetDialog({ isOpen: true, data: event }),
            },
          ],
        }}
        managementPermission={UserPermission.ManageRecognitionEvents}
        ManagementForm={ManageRecognition}
        IconHeader={RecognitionsIcon}
      >
        {({ fetchData }: any) => (
          <ResetLimitsDialog
            dialog={resetDialog}
            setDialog={setResetDialog}
            onResetSuccess={fetchData}
          />
        )}
      </CompanyAdminLayout>
    </>
  )
}

function ResetLimitsDialog({
  dialog,
  setDialog,
  onResetSuccess,
}: {
  dialog: { isOpen: boolean; data: any }
  setDialog: (value: { isOpen: boolean; data: any }) => void
  onResetSuccess: () => void
}) {
  const resetMutation = useMutation(
    (payload: any) => API.create('recognitionsResetLimits', payload),
    {
      onSuccess: () => {
        setDialog(initialResetDialog)
        onResetSuccess?.()
      },
    },
  )

  return (
    <ConfirmationDialog
      dialogOpen={dialog.isOpen}
      setDialogOpen={(value: boolean) =>
        setDialog(value ? dialog : initialResetDialog)
      }
      message={
        dialog.data
          ? `Reset the recognition count for "${dialog.data.name}"? The current usage counter will restart, but the limit rule and past recognition history stay intact.`
          : ''
      }
      item={dialog.data}
      actionProps={{
        label: 'Reset',
        color: 'error',
        isLoading: resetMutation.isLoading,
        onClick: (event: any) => resetMutation.mutate({ id: event?.id }),
      }}
    />
  )
}
